import { PathUtils } from 'Editor/services/_Common/Selection';
import { NodeUtils } from './NodeUtils';

export class NodeDataIterator
  implements DoDOCCommon.IIterator<Editor.Data.Node.DataPathInfo | null>
{
  private baseData: Editor.Data.Node.Data;

  private startPath: Editor.Selection.Path;
  private endPath: Editor.Selection.Path;
  private isCollapsed: boolean;

  private commonAncestorPath: Editor.Selection.Path;
  private commonAncestorData: Editor.Data.Node.Data | undefined;

  private remainingAncestorPath: Editor.Selection.Path | null = null;

  private startParentChildInfo: Editor.Data.Node.ParentChildInfo | null;

  private current: Editor.Data.Node.DataPathInfo | null;

  private nextData: Editor.Data.Node.DataPathInfo | null;
  private previousData: Editor.Data.Node.DataPathInfo | null;

  private options: {
    includeAncestors?: boolean;
  };

  constructor(
    baseData: Editor.Data.Node.Data,
    startPath: Editor.Selection.Path,
    endPath: Editor.Selection.Path,
    options: {
      includeAncestors?: boolean;
    } = {},
  ) {
    this.options = {
      includeAncestors: true,
      ...options,
    };

    this.baseData = baseData;

    this.isCollapsed = PathUtils.isPathEqual(startPath, endPath);

    this.startPath = NodeUtils.expandStartPath(this.baseData, startPath);
    this.endPath = endPath;

    // remove 'content' from paths
    if (this.startPath.includes('content')) {
      this.startPath = this.startPath.slice(0, this.startPath.indexOf('content'));
    }

    if (this.endPath.includes('content')) {
      this.endPath = this.endPath.slice(0, this.endPath.indexOf('content'));
    }

    this.commonAncestorPath = PathUtils.getCommonAncestorPath(this.startPath, this.endPath);
    this.commonAncestorData = NodeUtils.getChildDataByPath(
      this.baseData,
      this.commonAncestorPath,
      false,
    );

    this.remainingAncestorPath = [...this.startPath];

    this.startParentChildInfo = NodeUtils.getParentChildInfoByPath(
      this.baseData,
      this.startPath,
      false,
    );

    this.current = null;
    this.nextData = this.getNext();
    this.previousData = this.getPrevious();
  }

  private getNext(): Editor.Data.Node.DataPathInfo | null {
    if (this.current === null) {
      if (!this.commonAncestorData || !this.startParentChildInfo) {
        return null;
      }

      if (this.options.includeAncestors && this.remainingAncestorPath?.length) {
        // include ancestor elements
        return {
          data: this.baseData,
          path: [],
        };
      } else {
        if (
          PathUtils.isPathEqual(this.startParentChildInfo.parentPath, this.commonAncestorPath) &&
          !this.isCollapsed
        ) {
          return {
            data: this.commonAncestorData,
            path: this.commonAncestorPath,
          };
        } else {
          let firstChildPath = this.startPath.slice(0, this.commonAncestorPath.length + 2);

          if (firstChildPath.length < 2) {
            return null;
          }

          const firstChild = NodeUtils.getChildDataByPath(this.baseData, firstChildPath);
          if (firstChild) {
            return {
              data: firstChild,
              path: firstChildPath,
            };
          }
        }
      }
    } else {
      if (this.options.includeAncestors && this.remainingAncestorPath?.length) {
        // include ancestor elements
        const workingPath = this.remainingAncestorPath.splice(0, 2);
        const currentChild = NodeUtils.getChildDataByPath(this.current.data, workingPath);
        if (currentChild) {
          return {
            data: currentChild,
            path: [...this.current.path, ...workingPath],
          };
        }
      } else {
        // check if path is at end
        if (PathUtils.isPathEqual(this.current.path, this.endPath)) {
          return null;
        }

        // check if start path is contained in current path
        if (
          PathUtils.isChildPath(this.current.path, this.startPath) &&
          !NodeUtils.isTextData(this.current.data)
        ) {
          let childPath = this.startPath.slice(
            this.current.path.length,
            this.current.path.length + 2,
          );

          if (childPath.length > 0) {
            let childData = NodeUtils.getChildDataByPath(this.current.data, childPath);
            if (childData && childData.id !== this.current.data.id) {
              return {
                data: childData,
                path: [...this.current.path, ...childPath],
              };
            }
          }
        }

        // check if current node has child nodes
        if (
          !NodeUtils.isTextData(this.current.data) &&
          this.current.data.childNodes &&
          this.current.data.childNodes.length > 0
        ) {
          return {
            data: this.current.data.childNodes[0],
            path: [...this.current.path, 'childNodes', 0],
          };
        }

        let parentChildInfo = NodeUtils.getParentChildInfoByPath(this.baseData, this.current.path);
        // check if current node has next sibling
        if (
          parentChildInfo &&
          parentChildInfo.parentData.childNodes &&
          parentChildInfo.childIndex < parentChildInfo.parentData.childNodes.length - 1
        ) {
          let path: Editor.Selection.Path = [
            ...parentChildInfo.parentPath,
            'childNodes',
            parentChildInfo.childIndex + 1,
          ];
          return {
            data: parentChildInfo.parentData.childNodes[parentChildInfo.childIndex + 1],
            path,
          };
        }

        // check if parent node has next sibling
        const nextAncestor = NodeUtils.getNextAncestor(this.baseData, this.current.path);
        if (nextAncestor && PathUtils.comparePath(this.endPath, nextAncestor.path) >= 0) {
          return nextAncestor;
        }
      }
    }

    return null;
  }

  private getPrevious(): Editor.Data.Node.DataPathInfo | null {
    let workingData: Editor.Data.Node.DataPathInfo | null = null;

    // TODO: to be implemented

    return workingData;
  }

  reset() {
    this.current = null;
    this.nextData = this.getNext();
    this.previousData = this.getPrevious();
  }

  hasNext(): boolean {
    return this.nextData != null;
  }

  next(): Editor.Data.Node.DataPathInfo | null {
    if (this.nextData != null) {
      this.previousData = this.current;
      this.current = this.nextData;
      this.nextData = this.getNext();

      return this.current;
    }

    return null;
  }
  hasPrevious(): boolean {
    return this.previousData != null;
  }
  previous(): Editor.Data.Node.DataPathInfo | null {
    if (this.previousData != null) {
      this.nextData = this.current;
      this.current = this.previousData;
      this.previousData = this.getPrevious();

      return this.current;
    }

    return null;
  }
}
